import React, { useState, useEffect, useRef} from "react";
import { Row, Col, Button, Modal } from "react-bootstrap";
import { IconPPT, IconDownload, IconReportIssue } from "../../../icons/Icons";
import {
  generatePresignedUrl,
  bulkGeneratePresignedUrl,
} from "../../../api/common-store-api";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getGUID } from "../../../common";
import Loader from "../../../components/Loader/Loader";
 
const ImagePreview = ({
  actionReport,
  contentUrl,
  slideData,
  setContent,
  form,
  setIsFullScreen,
  isFullScreen,
}) => {
  const [contentUrlData, setContentUrlData] = useState(null);
  const location = useLocation();
  const userRole = useSelector(
    (state) => state.user.currentUser?.roles[0].name
  );
  const arrayDownloadableFiles = slideData?.downloadableFile;
  const [showLoader, setShowLoader] = useState(false);
 
  // 🔹 Zoom + drag state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const lastTranslate = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);
 
  // Helper: keep values inside limits
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
 
  const zoomIn = () => setScale((prev) => Math.min(prev + 0.1, 4));
  const zoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.1, 1);
      if (newScale === 1) {
        setTranslate({ x: 0, y: 0 });
        lastTranslate.current = { x: 0, y: 0 };
      }
      return newScale;
    });
  };
  const resetZoom = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    lastTranslate.current = { x: 0, y: 0 };
  };
 
  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
  };
 
  const handleMouseMove = (e) => {
    if (!dragging || scale <= 1) return;
 
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
 
    const container = containerRef.current?.getBoundingClientRect();
    if (!container) return;
 
    const imgWidth = container.width * scale;
    const imgHeight = container.height * scale;
 
    const maxX = (imgWidth - container.width) / 2;
    const maxY = (imgHeight - container.height) / 2;
 
    const newX = clamp(lastTranslate.current.x + dx, -maxX, maxX);
    const newY = clamp(lastTranslate.current.y + dy, -maxY, maxY);
 
    setTranslate({ x: newX, y: newY });
  };
 
  const handleMouseUp = () => {
    if (dragging) {
      lastTranslate.current = { ...translate };
      setDragging(false);
    }
  };
 
  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [dragging, translate, scale]);
 
  const getPreviewUrl = async () => {
    const getGuid = getGUID(contentUrl);
    let previewUrl = await generatePresignedUrl(getGuid);
    if (previewUrl?.data) {
      setShowLoader(true);
      setContentUrlData(previewUrl?.data);
      setShowLoader(false);
    }
  };
 
  const getBulkGeneratePresignedUrl = async () => {
    if (arrayDownloadableFiles?.length > 0) {
      let responseURLsDownloadable = await bulkGeneratePresignedUrl(
        arrayDownloadableFiles.map((value) => value.filePath)
      );
      console.log("responseURLsDownloadable = ", responseURLsDownloadable);
    }
  };
 
  useEffect(() => {
    getPreviewUrl();
    getBulkGeneratePresignedUrl();
  }, []);
 
  const handleIsFullScreen = () => setIsFullScreen(true);
  const handleCloseFullScreen = () => setIsFullScreen(false);
  return (
    <div className="pages h-100 w-100 d-flex flex-column">
      {showLoader ? (
        <div style={{ height: "calc(100vh - 274px)", width: "100%" }}>
          <Loader type="activity" />
        </div>
      ) : (
        <div className="pageOuter pb-0 h-100 d-flex flex-column">
          <Row className="g-0 h-100 flex-grow-1">
            <Col className="col-lg-12 h-100 d-flex justify-content-center align-items-center position-relative">
              {/* 🔹 Image preview with zoom + bounded drag */}
              <div
                ref={containerRef}
                className="imagePreview"
                style={{
                  overflow: "hidden",
                  maxHeight: "100%",
                  maxWidth: "100%",
                  cursor:
                    scale > 1 ? (dragging ? "grabbing" : "grab") : "default",
                }}
                onMouseDown={handleMouseDown}
              >
                <img
                  src={contentUrlData}
                  alt="contentImage"
                  style={{
                    transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                    transition: dragging
                      ? "none"
                      : "transform 0.2s ease-in-out",
                    transformOrigin: "center center",
                    display: "block",
                    margin: "0 auto",
                    maxWidth: "100%",
                    height: "auto",
                  }}
                  draggable={false}
                />
              </div>
              {form === false && (
                <div className="d-flex justify-content-center align-items-center imageZoomControls">
                  <div className="text-sm font-medium text-white">
                    {Math.round(scale * 100)}%
                  </div>
                  <button onClick={zoomOut} disabled={scale <= 1}>
                    <svg
                      width="31"
                      height="31"
                      viewBox="0 0 31 31"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        opacity="0.5"
                        x="0.0908203"
                        y="0.0908813"
                        width="30"
                        height="30"
                        rx="5.625"
                        fill="#232323"
                      />
                      <path
                        d="M12.75 14.775H16.5"
                        stroke="white"
                        stroke-width="1.125"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M14.625 21.75C18.56 21.75 21.75 18.56 21.75 14.625C21.75 10.69 18.56 7.5 14.625 7.5C10.69 7.5 7.5 10.69 7.5 14.625C7.5 18.56 10.69 21.75 14.625 21.75Z"
                        stroke="white"
                        stroke-width="1.125"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M22.5 22.5L21 21"
                        stroke="white"
                        stroke-width="1.125"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                  <button onClick={zoomIn} disabled={scale >= 4}>
                    <svg
                      width="31"
                      height="31"
                      viewBox="0 0 31 31"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        opacity="0.5"
                        x="0.0908203"
                        y="0.0908813"
                        width="30"
                        height="30"
                        rx="5.625"
                        fill="#232323"
                      />
                      <path
                        d="M12.9004 14.775H16.6504"
                        stroke="white"
                        stroke-width="1.125"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M14.7744 16.65V12.9"
                        stroke="white"
                        stroke-width="1.125"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M14.625 21.75C18.56 21.75 21.75 18.56 21.75 14.625C21.75 10.69 18.56 7.5 14.625 7.5C10.69 7.5 7.5 10.69 7.5 14.625C7.5 18.56 10.69 21.75 14.625 21.75Z"
                        stroke="white"
                        stroke-width="1.125"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M22.5 22.5L21 21"
                        stroke="white"
                        stroke-width="1.125"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="border-start border-gray-1200"
                    style={{ paddingLeft: "12px" }}
                    onClick={
                      isFullScreen ? handleCloseFullScreen : handleIsFullScreen
                    }
                  >
                    {isFullScreen ? (
                      <svg
                        width="31"
                        height="31"
                        viewBox="0 0 31 31"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          opacity="0.5"
                          x="0.0908203"
                          y="0.0909424"
                          width="30"
                          height="30"
                          rx="5.625"
                          fill="#232323"
                        />
                        <path
                          d="M8.925 22.125L7.875 21.075L10.2 18.75H8.25V17.25H12.75V21.75H11.25V19.8L8.925 22.125ZM21.075 22.125L18.75 19.8V21.75H17.25V17.25H21.75V18.75H19.8L22.125 21.075L21.075 22.125ZM8.25 12.75V11.25H10.2L7.875 8.925L8.925 7.875L11.25 10.2V8.25H12.75V12.75H8.25ZM17.25 12.75V8.25H18.75V10.2L21.075 7.875L22.125 8.925L19.8 11.25H21.75V12.75H17.25ZM15 16.5C14.5875 16.5 14.2344 16.3531 13.9406 16.0594C13.6469 15.7656 13.5 15.4125 13.5 15C13.5 14.5875 13.6469 14.2344 13.9406 13.9406C14.2344 13.6469 14.5875 13.5 15 13.5C15.4125 13.5 15.7656 13.6469 16.0594 13.9406C16.3531 14.2344 16.5 14.5875 16.5 15C16.5 15.4125 16.3531 15.7656 16.0594 16.0594C15.7656 16.3531 15.4125 16.5 15 16.5Z"
                          fill="white"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="31"
                        height="31"
                        viewBox="0 0 31 31"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          opacity="0.5"
                          x="0.0908203"
                          y="0.0908813"
                          width="30"
                          height="30"
                          rx="5.625"
                          fill="#232323"
                        />
                        <g clip-path="url(#clip0_28609_8480)">
                          <path
                            d="M21.0908 9.09088L9.09082 21.0909"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M21.0908 12.8409V9.09088H17.3408"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M9.09082 17.3409V21.0909H12.8408"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M9.09082 9.09088L21.0908 21.0909"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M9.09082 12.8409V9.09088H12.8408"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M21.0908 17.3409V21.0909H17.3408"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_28609_8480">
                            <rect
                              width="18"
                              height="18"
                              fill="white"
                              transform="translate(6.09082 6.09088)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    )}
                  </button>
                  {/* ) : (
                    <button
                      className="border-start border-gray-1200"
                      style={{ paddingLeft: "12px" }}
                      onClick={handleCloseFullScreen}
                    >
                      <svg
                        width="31"
                        height="31"
                        viewBox="0 0 31 31"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          opacity="0.5"
                          x="0.0908203"
                          y="0.0908813"
                          width="30"
                          height="30"
                          rx="5.625"
                          fill="#232323"
                        />
                        <g clip-path="url(#clip0_28609_8480)">
                          <path
                            d="M9.09082 9.09088L21.0908 21.0909"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M17.3408 9.09088H21.0908V12.8409"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M12.8408 21.0909H9.09082V17.3409"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M21.0908 9.09088L9.09082 21.0909"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M12.8408 9.09088H9.09082V12.8409"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M17.3408 21.0909H21.0908V17.3409"
                            stroke="white"
                            stroke-width="1.35"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_28609_8480">
                            <rect
                              width="18"
                              height="18"
                              fill="white"
                              transform="translate(6.09082 6.09088)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  )} */}
                </div>
              )}
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};
 
export default ImagePreview;